'use client';

import { AlertBanner } from '../../components/alert-banner';
import { SettingsEditDialog } from '../settings/SettingsEditDialog';
import { VStack } from '@terra/ui-styled-system';
import { useTranslations } from 'next-intl';
import {
  ScanEffortSlider,
  type ScanEffortSliderOption,
} from './ScanEffortSlider';

type ScanEffortEditDialogProps<TValue extends string> = {
  draft: TValue | null;
  current: TValue;
  options: readonly ScanEffortSliderOption<TValue>[];
  info: string;
  loading: boolean;
  onDraftChange: (value: TValue | null) => void;
  onSubmit: (value: TValue) => Promise<void>;
};

export function ScanEffortEditDialog<TValue extends string>({
  draft,
  current,
  options,
  info,
  loading,
  onDraftChange,
  onSubmit,
}: ScanEffortEditDialogProps<TValue>) {
  const t = useTranslations('designSystem.scanEffort');
  const tCommon = useTranslations('designSystem.common');

  return (
    <SettingsEditDialog
      open={draft !== null}
      title={t('editTitle')}
      onOpenChange={(open) => {
        if (!open) {
          onDraftChange(null);
        }
      }}
      onSubmit={async () => {
        if (draft !== null) {
          await onSubmit(draft);
        }
      }}
      loading={loading}
      isSubmitDisabled={draft === null || draft === current}
      submitText={tCommon('saveChanges')}
    >
      <VStack alignItems="stretch" gap={4} w="full" pt={2}>
        <AlertBanner kind="info" description={info} />
        <ScanEffortSlider
          options={options}
          value={draft ?? current}
          ariaLabel={t('editTitle')}
          disabled={loading}
          onCommit={onDraftChange}
        />
      </VStack>
    </SettingsEditDialog>
  );
}
